export type SizeMappingEntry = {
    centraSizeValue: string
    generalSizes: string[]
}

export type FacetMappings = {
    generalSizeOrder: string[]
    generalSizeToSizeMap: Record<string, string[]>
    allowedFacets: Set<string>
}

export function buildFacetMappings(
    generalSizes: string[] | null | undefined,
    sizeMappings: SizeMappingEntry[] | null | undefined,
    allowedFacets: string[] | null | undefined
): FacetMappings {
    const generalSizeToSizeMap: Record<string, string[]> = {}

    if (sizeMappings) {
        for (const mapping of sizeMappings) {
            for (const generalSize of mapping.generalSizes) {
                if (!generalSizeToSizeMap[generalSize]) {
                    generalSizeToSizeMap[generalSize] = []
                }
                generalSizeToSizeMap[generalSize].push(mapping.centraSizeValue)
            }
        }
        for (const sizes of Object.values(generalSizeToSizeMap)) {
            sizes.sort()
        }
    }

    return {
        generalSizeOrder: generalSizes ?? [],
        generalSizeToSizeMap,
        allowedFacets: new Set(allowedFacets ?? [])
    }
}

export type GeneralSizeBucket = {
    /** Canonical general size label (e.g. "M", "ONE SIZE", "YOUTH"). */
    generalSize: string
    /** Position of this bucket in `generalSizeOrder`. */
    index: number
    /**
     * Sum of `meta.counts` from Depict for every Centra size that maps to
     * this bucket. `0` means no product in the current listing has a size
     * that rolls up to this bucket — the UI should render it as disabled.
     */
    count: number
}

/**
 * Roll up Depict's raw Centra-level size facet into the canonical general-size
 * buckets defined in Sanity (`XXS, XS, S, M, L, XL, XXL, ONE SIZE, YOUTH`).
 *
 * Depict returns one entry per Centra-specific size (e.g. `"m"`, `"M-L"`,
 * `"M/55-58"`, `"Regular fit"`, `"130/8Y"`). Many of those map to a single
 * general size — so to show a meaningful count next to "M" we sum the counts
 * from every Centra size whose mapping includes `"M"`. Matching is
 * case-insensitive because Depict is inconsistent (`"m"` vs `"One Size"` vs
 * `"Regular fit"` vs `"Wide Fit"`).
 *
 * The inverse transform — expanding a selected general size back to its
 * Centra values before sending the filter to Depict — lives in
 * `transformedFilterOptions` in `app/(site)/_components/Organisms/ProductGrids/helpers.ts`.
 */
export function buildGeneralSizeBuckets(
    depictValues: readonly unknown[] | undefined,
    depictCounts: readonly number[] | undefined,
    mappings: FacetMappings
): GeneralSizeBucket[] {
    const { generalSizeOrder, generalSizeToSizeMap } = mappings

    const countByCentraSize = new Map<string, number>()
    ;(depictValues ?? []).forEach((value, index) => {
        countByCentraSize.set(String(value).toLowerCase(), depictCounts?.[index] ?? 0)
    })

    return generalSizeOrder.map((generalSize, index) => {
        const centraSizes = generalSizeToSizeMap[generalSize] ?? []
        const count = centraSizes.reduce(
            (sum, centraSize) =>
                sum + (countByCentraSize.get(centraSize.toLowerCase()) ?? 0),
            0
        )
        return { generalSize, index, count }
    })
}
